// ── RealtimeStatus ─────────────────────────────────────────────────
//
// Header badge showing live socket.io connection state. Click opens
// a small popover with the current status + WhatsApp realtime
// preferences (browser notifications, sound). The popover doubles as
// a reconnect button when offline.
//
// Mirrors the NotificationBell pattern (outside-click ref, click-toggle
// open) rather than pulling in @radix-ui/react-popover just for this.

import { useEffect, useRef, useState } from 'react';
import { Wifi, WifiOff, Loader2, RotateCw, Bell, BellOff, Volume2, VolumeX } from 'lucide-react';
import { realtime, RealtimeStatus as RealtimeStatusValue } from '@/lib/realtime';
import {
  broadcastPrefsChange,
  getBrowserNotificationPermission,
  getRealtimePrefs,
  onPrefsChange,
  playInboundPing,
  requestBrowserNotificationPermission,
  setBrowserNotificationsEnabled,
  setSoundAlertsEnabled,
} from '@/features/whatsapp/realtime/notification-prefs';

const COPY: Record<
  RealtimeStatusValue,
  { label: string; tooltip: string; dot: string; row: string }
> = {
  connecting: {
    label: 'Connecting…',
    tooltip: 'Establishing realtime connection. New WhatsApp messages will appear in a moment.',
    dot: 'bg-amber-400',
    row: 'text-amber-700 bg-amber-50',
  },
  connected: {
    label: 'Live',
    tooltip: 'Realtime is connected — new WhatsApp messages appear instantly.',
    dot: 'bg-emerald-500',
    row: 'text-emerald-700 bg-emerald-50',
  },
  reconnecting: {
    label: 'Reconnecting…',
    tooltip: 'Lost connection to the realtime server. Auto-retrying with exponential backoff.',
    dot: 'bg-amber-500',
    row: 'text-amber-700 bg-amber-50',
  },
  offline: {
    label: 'Offline',
    tooltip: 'Realtime is offline. Inbox will fall back to polling.',
    dot: 'bg-red-500',
    row: 'text-red-700 bg-red-50',
  },
};

export function RealtimeStatus() {
  const [status, setStatus] = useState<RealtimeStatusValue>(realtime.getStatus());
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => realtime.subscribeStatus(setStatus), []);

  // Outside-click to close — mirrors NotificationBell's pattern.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const copy = COPY[status];
  const Icon =
    status === 'connecting' || status === 'reconnecting'
      ? Loader2
      : status === 'connected'
        ? Wifi
        : WifiOff;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        title={copy.tooltip}
        aria-label={`Realtime status: ${copy.label}`}
        className="flex items-center gap-1.5 rounded-md px-1.5 py-1 hover:bg-gray-100 transition-colors"
        type="button"
      >
        <span className="relative flex h-2 w-2">
          {status === 'connected' && (
            <span className={`absolute inline-flex h-full w-full animate-ping rounded-full ${copy.dot} opacity-50`} />
          )}
          <span className={`relative inline-flex h-2 w-2 rounded-full ${copy.dot}`} />
        </span>
        <span className="hidden sm:inline-flex items-center gap-1 text-xs text-slate-500">
          <Icon
            className={`h-3 w-3 ${
              status === 'connecting' || status === 'reconnecting' ? 'animate-spin' : ''
            }`}
          />
          <span>{copy.label}</span>
        </span>
      </button>

      {open && (
        <div className="absolute right-0 top-11 w-[320px] bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100">
            <div className={`flex items-center gap-2 rounded-md px-2 py-1.5 text-xs font-medium ${copy.row}`}>
              <span className={`h-2 w-2 rounded-full ${copy.dot}`} />
              <span>{copy.label}</span>
              {(status === 'offline' || status === 'reconnecting') && (
                <button
                  onClick={() => realtime.reconnect()}
                  className="ml-auto inline-flex items-center gap-1 text-[#6366F1] hover:text-[#4F46E5] font-medium"
                  type="button"
                >
                  <RotateCw className="h-3 w-3" /> Reconnect now
                </button>
              )}
            </div>
            <p className="mt-2 text-[11px] leading-relaxed text-slate-500">{copy.tooltip}</p>
          </div>

          <RealtimePrefsRows />
        </div>
      )}
    </div>
  );
}

// ── Prefs rows (exported separately so SettingsPage can also embed) ─

export function RealtimePrefsRows() {
  const [prefs, setPrefs] = useState(getRealtimePrefs());
  const [permission, setPermission] = useState(getBrowserNotificationPermission());

  useEffect(() => onPrefsChange(() => setPrefs(getRealtimePrefs())), []);

  const toggleNotifications = async () => {
    // Asking the OS for permission requires a user gesture (this click).
    // We chain: if permission isn't granted yet, ask; only flip the
    // setting on if granted. If user denies, leave setting off + show
    // the denied state in the row.
    if (!prefs.browserNotifications) {
      const result = await requestBrowserNotificationPermission();
      setPermission(result);
      if (result !== 'granted') {
        // Don't flip setting on — would silently fail later.
        return;
      }
    }
    const next = !prefs.browserNotifications;
    setBrowserNotificationsEnabled(next);
    broadcastPrefsChange();
    setPrefs(getRealtimePrefs());
  };

  const toggleSound = () => {
    const next = !prefs.soundAlerts;
    setSoundAlertsEnabled(next);
    broadcastPrefsChange();
    setPrefs(getRealtimePrefs());
    // Play a sample beep when turning ON so the user knows what to expect.
    if (next) playInboundPing();
  };

  return (
    <div className="px-2 py-2">
      <Toggle
        icon={prefs.browserNotifications ? <Bell className="h-4 w-4" /> : <BellOff className="h-4 w-4" />}
        title="Browser notifications"
        subtitle={
          permission === 'unsupported'
            ? 'Your browser does not support notifications'
            : permission === 'denied'
              ? 'Blocked — change in browser site settings'
              : prefs.browserNotifications
                ? 'On — banner appears when the tab is hidden'
                : 'Off — no banner for new WhatsApp messages'
        }
        on={prefs.browserNotifications}
        disabled={permission === 'unsupported' || permission === 'denied'}
        onToggle={toggleNotifications}
      />
      <Toggle
        icon={prefs.soundAlerts ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
        title="Sound alert"
        subtitle={
          prefs.soundAlerts ? 'On — short ping on every new message' : 'Off — silent inbox'
        }
        on={prefs.soundAlerts}
        onToggle={toggleSound}
      />
    </div>
  );
}

// ── Small toggle row ────────────────────────────────────────────────

function Toggle({
  icon,
  title,
  subtitle,
  on,
  disabled,
  onToggle,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  on: boolean;
  disabled?: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      onClick={onToggle}
      disabled={disabled}
      className={`flex items-center gap-3 w-full rounded-lg px-2 py-2 text-left transition-colors ${
        disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-slate-50'
      }`}
      type="button"
    >
      <span className={`flex h-8 w-8 items-center justify-center rounded-md ${on ? 'bg-indigo-100 text-indigo-600' : 'bg-slate-100 text-slate-500'}`}>
        {icon}
      </span>
      <span className="flex-1 min-w-0">
        <span className="block text-sm font-medium text-slate-800 truncate">{title}</span>
        <span className="block text-[11px] text-slate-500 truncate">{subtitle}</span>
      </span>
      <span
        className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full transition-colors ${
          on ? 'bg-indigo-500' : 'bg-slate-300'
        }`}
        aria-pressed={on}
      >
        <span
          className={`absolute top-0.5 inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${
            on ? 'translate-x-4' : 'translate-x-0.5'
          }`}
        />
      </span>
    </button>
  );
}
